// Browser-only measurement. Public Pixel ID; no conversion API keys or user data.
// https://developers.openai.com/ads/measurement-pixel
export const OPENAI_PIXEL_ID = "Pj6CoHQ9sh2wY3TZ1pkS6Y"
const CONSENT_COOKIE = "seam_openai_ads_consent"
const CONSENT_AGE = 6 * 30 * 24 * 60 * 60

type Pixel = ((...args: unknown[]) => void) & { q?: IArguments[] }

declare global {
  interface Window {
    oaiq?: Pixel
    __seamOpenAIAdsLoaded?: boolean
  }
}

export function isOpenAIAdsProduction(): boolean {
  return (
    process.env.NODE_ENV === "production" &&
    typeof window !== "undefined" &&
    ["seam.co", "www.seam.co", "console.seam.co"].includes(
      window.location.hostname
    )
  )
}

function cookie(name: string): string | undefined {
  return document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${name}=`))
    ?.slice(name.length + 1)
}

function sharedCookie(name: string, value: string, age: number) {
  document.cookie = `${name}=${value}; Domain=seam.co; Path=/; Max-Age=${age}; SameSite=Lax; Secure`
}

export function hasAdvertisingConsent(): boolean {
  if (!isOpenAIAdsProduction()) return false
  if (
    (navigator as Navigator & { globalPrivacyControl?: boolean })
      .globalPrivacyControl
  )
    return false
  const grantedAt = Number(cookie(CONSENT_COOKIE))
  const age = Date.now() - grantedAt
  return grantedAt > 0 && age >= 0 && age <= CONSENT_AGE * 1000
}

export const hasOpenAIAdsConsent = hasAdvertisingConsent

/** Share only the advertising decision across marketing and console subdomains. */
export function setOpenAIAdsConsent(enabled: boolean, decidedAt = Date.now()) {
  if (!isOpenAIAdsProduction()) return
  sharedCookie(
    CONSENT_COOKIE,
    enabled ? String(decidedAt) : "",
    enabled ? CONSENT_AGE : 0
  )
  if (!hasOpenAIAdsConsent()) {
    window.oaiq?.("consent", false)
    try {
      localStorage.removeItem(PENDING_SIGNUP)
    } catch {
      /* Storage may be unavailable. */
    }
    sharedCookie("__oppref", "", 0)
    sharedCookie("__obref", "", 0)
    return
  }
  initOpenAIAds()
}

/** Queue synchronously so conversions cannot race the async SDK download. */
export function initOpenAIAds(): boolean {
  if (!hasOpenAIAdsConsent()) {
    window.oaiq?.("consent", false)
    return false
  }
  if (!window.oaiq) {
    const queue: Pixel = function () {
      // The documented SDK shim queues Arguments objects.
      // eslint-disable-next-line prefer-rest-params
      queue.q!.push(arguments)
    }
    queue.q = []
    window.oaiq = queue
    queue("consent", true)
    queue("init", { pixelId: OPENAI_PIXEL_ID })
    const script = document.createElement("script")
    script.async = true
    script.src = "https://bzrcdn.openai.com/sdk/oaiq.min.js"
    script.onload = () => {
      window.__seamOpenAIAdsLoaded = true
      finishPendingSignup()
    }
    document.head.appendChild(script)
  } else {
    window.oaiq("consent", true)
  }
  // The SDK's first-party attribution cookie must survive www -> console.
  // Capture only after advertising consent; never extend it on ordinary visits.
  const oppref = new URL(window.location.href).searchParams.get("oppref")
  if (oppref && oppref.length <= 4096) {
    sharedCookie("__oppref", encodeURIComponent(oppref), 30 * 24 * 60 * 60)
  }
  dispatchPendingSignup()
  return true
}

const measured = new Set<string>()
const PENDING_SIGNUP = "seam_openai_signup_pending"
interface PendingSignup {
  key: string
  eventId: string
}

function pendingSignup(): PendingSignup | null {
  const raw = localStorage.getItem(PENDING_SIGNUP)
  return raw ? (JSON.parse(raw) as PendingSignup) : null
}

function finishPendingSignup() {
  try {
    const pending = pendingSignup()
    if (!pending || !measured.has(pending.key)) return
    localStorage.setItem(pending.key, "1")
    localStorage.removeItem(PENDING_SIGNUP)
  } catch {
    /* Storage may be unavailable. */
  }
}

function dispatchPendingSignup() {
  try {
    const pending = pendingSignup()
    if (!pending || measured.has(pending.key)) return
    window.oaiq!(
      "measure",
      "registration_completed",
      { type: "customer_action" },
      { event_id: pending.eventId }
    )
    measured.add(pending.key)
    if (window.__seamOpenAIAdsLoaded) finishPendingSignup()
  } catch {
    /* Advertising must never block account creation. */
  }
}

/** Called only for the API's confirmed needs_onboarding state, before it clears. */
export function measureOpenAISignup(user: {
  user_id: string
  needs_onboarding?: boolean
}) {
  try {
    if (user.needs_onboarding !== true || !initOpenAIAds()) return
    const key = `seam_openai_signup:${user.user_id}`
    if (measured.has(key) || localStorage.getItem(key)) return
    const eventId = crypto.randomUUID()
    // A Pages -> App Router navigation can unload the SDK before it downloads.
    // Keep the event until SDK load; the next entry queues the same event ID.
    localStorage.setItem(PENDING_SIGNUP, JSON.stringify({ key, eventId }))
    dispatchPendingSignup()
  } catch {
    // Advertising must never interrupt account creation (including blocked storage).
  }
}
